import { useEffect, useState, useRef } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Check, Ban, Paperclip, Send, Trash2, ShieldCheck, ImageIcon, ExternalLink, Link2, Building2, Plus, X, Pencil } from "lucide-react";
import AttachmentThumb from "@/components/AttachmentThumb";
import DriveImport from "@/components/DriveImport";
import SupplierPicker from "@/components/SupplierPicker";
import CategoryPicker from "@/components/CategoryPicker";
import TagControl from "@/components/TagControl";
import { timeAgo, money, cogsOf, marginPct, tagMeta, productEconomics } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";

const NONE = "__none__";

function ProductImage({ image, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u, active = true;
    if (image?.storage_path) {
      fetch(`${API}/files/${image.storage_path}`, { credentials: "include" }).then((r) => r.blob()).then((b) => { if (active) { u = URL.createObjectURL(b); setUrl(u); } });
    }
    return () => { active = false; if (u) URL.revokeObjectURL(u); };
  }, [image?.storage_path]);
  if (url) return <img src={url} alt="product" className={className} />;
  return <div className={`${className} flex items-center justify-center bg-zinc-100`}><Loader2 className="h-4 w-4 animate-spin text-zinc-400" /></div>;
}

export default function ProductDrawer({ productId, onClose, statuses, categories, users, suppliers, reloadSuppliers, reloadCategories, onChange }) {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [editName, setEditName] = useState(false);
  const [nameVal, setNameVal] = useState("");
  const [editPricing, setEditPricing] = useState(false);
  const [pr, setPr] = useState({ rrp: "", costs: [], product_url: "", shelf: "" });
  const fileRef = useRef();
  const imgRef = useRef();

  const canManage = user?.role === "admin" || user?.role === "manager";
  const load = () => api.get(`/products/${productId}`).then((r) => {
    setP(r.data);
    setPr({ rrp: r.data.rrp ?? "", units: r.data.units ?? 1, costs: (r.data.costs || []).map((c) => ({ label: c.label, amount: c.amount })), product_url: r.data.product_url || "", shelf: r.data.shelf || "" });
  });
  useEffect(() => { load(); }, [productId]);

  const isApprover = p?.approver_ids?.includes(user?.id);
  const myDecision = p?.decisions?.find((d) => d.user_id === user?.id);

  const patch = async (body) => {
    try { const { data } = await api.patch(`/products/${productId}`, body); setP(data); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveName = async () => {
    const v = nameVal.trim();
    if (!v) return toast.error("Name is required");
    await patch({ name: v });
    setEditName(false);
    toast.success("Name updated");
  };

  const savePricing = async () => {
    await patch({
      rrp: pr.rrp === "" ? null : parseFloat(pr.rrp),
      units: parseInt(pr.units) || 1,
      costs: pr.costs.filter((c) => c.label.trim() || c.amount !== "").map((c) => ({ label: c.label || "Cost", amount: parseFloat(c.amount) || 0 })),
      product_url: pr.product_url,
      shelf: pr.shelf,
    });
    setEditPricing(false);
    toast.success("Saved");
  };

  const saveTag = async (tag, tagNote) => {
    try { const { data } = await api.post(`/products/${productId}/assessment`, { tag, note: tagNote }); setP(data); onChange?.(); toast.success("Tag updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const decide = async (kind) => {
    if (kind === "reject" && !reason.trim()) return toast.error("A reason is required to reject");
    setBusy(true);
    try { const { data } = await api.post(`/products/${productId}/${kind}`, { reason }); setP(data); setReason(""); onChange?.(); toast.success(kind === "approve" ? "Product approved" : "Product rejected"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/products/${productId}/notes`, { message: note }); setP(data); setNote(""); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); setBusy(true);
    try { const { data } = await api.post(`/products/${productId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setP(data); onChange?.(); toast.success("Attachment added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); setBusy(true);
    try { const { data } = await api.post(`/products/${productId}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setP(data); onChange?.(); toast.success("Image updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); if (imgRef.current) imgRef.current.value = ""; }
  };

  const importImage = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/products/${productId}/import-image`, {}); setP(data); onChange?.(); toast.success("Image imported from URL"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const [imgSearchOpen, setImgSearchOpen] = useState(false);
  const [imgSearching, setImgSearching] = useState(false);
  const [imgResults, setImgResults] = useState([]);
  const searchGoogleImages = async () => {
    setImgSearchOpen(true); setImgSearching(true); setImgResults([]);
    try {
      const { data } = await api.get(`/products/image-search`, { params: { q: p.name } });
      setImgResults(data.results || []);
      if (!data.results?.length) toast.info("No images found");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setImgSearchOpen(false); }
    finally { setImgSearching(false); }
  };
  const pickGoogleImage = async (url) => {
    setBusy(true);
    try { const { data } = await api.post(`/products/${productId}/import-image`, { url }); setP(data); onChange?.(); setImgSearchOpen(false); toast.success("Image set"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/products/${productId}`); toast.success("Product deleted"); onChange?.(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const notes = [...(p?.notes || [])].reverse();
  const econ = productEconomics(p || {});
  const editCogs = cogsOf(pr.costs.map((c) => ({ amount: parseFloat(c.amount) || 0 })));

  const addCost = () => setPr((x) => ({ ...x, costs: [...x.costs, { label: "", amount: "" }] }));
  const updCost = (i, k, v) => setPr((x) => ({ ...x, costs: x.costs.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) }));
  const rmCost = (i) => setPr((x) => ({ ...x, costs: x.costs.filter((_, idx) => idx !== i) }));

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto rounded-none border-l border-zinc-200 p-0 sm:max-w-2xl">
        <SheetTitle className="sr-only">Product details</SheetTitle>
        {!p ? (
          <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#e84e34]" /></div>
        ) : (
          <div className="flex min-h-full flex-col">
            <div className="border-b border-zinc-200 p-6">
              <div className="flex items-start justify-between pr-8">
                <div className="flex gap-4">
                  {p.image ? <ProductImage image={p.image} className="h-16 w-16 flex-shrink-0 border border-zinc-200 object-cover" /> : <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center border border-zinc-200 bg-zinc-50 text-zinc-300"><ImageIcon className="h-6 w-6" /></div>}
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-[#e84e34]">Product</p>
                    {editName ? (
                      <div className="mt-1 flex items-center gap-2">
                        <input autoFocus value={nameVal} onChange={(e) => setNameVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditName(false); }} data-testid="edit-product-name-input" className="w-full border border-zinc-200 px-2 py-1 font-heading text-xl font-bold tracking-tight text-zinc-900 outline-none focus:border-[#e84e34]" />
                        <button onClick={saveName} data-testid="save-product-name" className="flex-shrink-0 bg-zinc-900 px-2 py-1.5 text-xs font-medium text-white hover:bg-zinc-800">Save</button>
                        <button onClick={() => setEditName(false)} className="flex-shrink-0 text-zinc-400 hover:text-[#e84e34]"><X className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      <h2 className="mt-1 flex items-center gap-2 font-heading text-xl font-bold tracking-tight text-zinc-900" data-testid="drawer-product-title">
                        {p.name}
                        {canManage && <button onClick={() => { setNameVal(p.name); setEditName(true); }} data-testid="edit-product-name-button" title="Edit name" className="text-zinc-300 hover:text-[#e84e34]"><Pencil className="h-3.5 w-3.5" /></button>}
                      </h2>
                    )}
                    {tagMeta(p.tag) && <span className="mt-2 inline-block border px-2 py-0.5 text-xs font-medium uppercase tracking-wider" style={{ color: tagMeta(p.tag).color, borderColor: tagMeta(p.tag).color, background: tagMeta(p.tag).bg }}>{tagMeta(p.tag).label}</span>}
                  </div>
                </div>
                {canManage && <button onClick={remove} data-testid="delete-product-button" className="p-1 text-zinc-400 hover:text-[#e84e34]"><Trash2 className="h-4 w-4" /></button>}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => imgRef.current?.click()} data-testid="product-image-upload" className="flex items-center gap-1 border border-zinc-200 px-2 py-1 text-xs text-zinc-600 hover:border-[#e84e34]"><ImageIcon className="h-3 w-3" /> {p.image ? "Change image" : "Add image"}</button>
                <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} />
                <button onClick={searchGoogleImages} disabled={busy} data-testid="product-image-google" className="flex items-center gap-1 border border-zinc-200 px-2 py-1 text-xs text-zinc-600 hover:border-[#e84e34] disabled:opacity-60"><ImageIcon className="h-3 w-3" /> Search Google images</button>
                <DriveImport targetType="product_image" targetId={productId} label="Drive image" variant="outline" onImported={(parent) => { if (parent) setP(parent); onChange?.(); }} />
                {p.product_url && <button onClick={importImage} disabled={busy} data-testid="product-image-import" className="flex items-center gap-1 border border-zinc-200 px-2 py-1 text-xs text-zinc-600 hover:border-[#e84e34] disabled:opacity-60"><Link2 className="h-3 w-3" /> Import from URL</button>}
                {p.product_url && <a href={p.product_url} target="_blank" rel="noreferrer" data-testid="product-url-link" className="flex items-center gap-1 border border-zinc-200 px-2 py-1 text-xs text-[#e84e34] hover:underline"><ExternalLink className="h-3 w-3" /> View product</a>}
              </div>
              {imgSearchOpen && (
                <div className="mt-3 border border-zinc-200 bg-zinc-50 p-3" data-testid="google-image-results">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">Google images for "{p.name}"</p>
                    <button onClick={() => setImgSearchOpen(false)} className="text-zinc-400 hover:text-[#e84e34]"><X className="h-4 w-4" /></button>
                  </div>
                  {imgSearching ? (
                    <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#e84e34]" /></div>
                  ) : imgResults.length === 0 ? (
                    <p className="py-4 text-center text-xs text-zinc-400">No results</p>
                  ) : (
                    <div className="grid grid-cols-5 gap-2">
                      {imgResults.map((r, i) => (
                        <button key={i} onClick={() => pickGoogleImage(r.image_url)} disabled={busy} data-testid={`google-image-${i}`} title={r.title} className="group relative aspect-square overflow-hidden border border-zinc-200 bg-white hover:border-[#e84e34] disabled:opacity-60">
                          <img src={r.thumbnail_url} alt={r.title} className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Assessment tag */}
              <div className="mt-4 border border-zinc-200 bg-zinc-50 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-600">Assessment</p>
                <TagControl tag={p.tag} note={p.tag_note} onSave={saveTag} testidPrefix="product-tag" />
                {p.tag_note && (p.tag === "potential" || p.tag === "rejected") && <p className="mt-2 border-l-2 border-[#e84e34] bg-white px-2 py-1 text-xs text-zinc-600">{p.tag_note}</p>}
              </div>

              {isApprover ? (
                <div className="mt-4 border border-zinc-200 bg-zinc-50 p-3">
                  <p className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-zinc-600"><ShieldCheck className="h-3 w-3" /> You are an approver</p>
                  {myDecision && <p className="mb-2 text-xs text-zinc-500">Your decision: <span className="font-medium" style={{ color: myDecision.decision === "approved" ? "#16a34a" : "#e84e34" }}>{myDecision.decision}</span></p>}
                  <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="decision-reason" placeholder="Reason (required to reject)" className="mb-2 w-full border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
                  <div className="flex gap-2">
                    <button onClick={() => decide("approve")} disabled={busy} data-testid="approve-button" className="flex items-center gap-1 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-60"><Check className="h-3 w-3" /> Approve</button>
                    <button onClick={() => decide("reject")} disabled={busy} data-testid="reject-button" className="flex items-center gap-1 bg-[#e84e34] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#c73e26] disabled:opacity-60"><Ban className="h-3 w-3" /> Reject</button>
                  </div>
                </div>
              ) : (
                <p className="mt-3 text-xs text-zinc-400">Only assigned approvers can approve or reject this product.</p>
              )}
            </div>

            <div className="grid flex-1 grid-cols-1 lg:grid-cols-2">
              <div className="space-y-5 border-r border-zinc-200 p-6">
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Description</label>
                  <p className="whitespace-pre-wrap text-sm text-zinc-700">{p.description || "—"}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Status</label>
                    <Select value={p.status_id || NONE} onValueChange={(v) => patch({ status_id: v === NONE ? null : v })}>
                      <SelectTrigger data-testid="drawer-product-status" className="rounded-none"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent><SelectItem value={NONE}>None</SelectItem>{statuses.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <CategoryPicker value={p.category_id} onChange={(id) => patch({ category_id: id })} categories={categories} reload={reloadCategories} canAdd={canManage} />
                </div>

                <SupplierPicker value={p.supplier_id} onChange={(id) => patch({ supplier_id: id })} suppliers={suppliers} reload={reloadSuppliers} />
                {p.supplier && (
                  <div className="border border-zinc-200 bg-zinc-50 p-3 text-sm" data-testid="supplier-details">
                    <p className="flex items-center gap-1 font-medium text-zinc-900"><Building2 className="h-3.5 w-3.5" /> {p.supplier.company_name}</p>
                    <div className="mt-1 space-y-0.5 text-xs text-zinc-500">
                      {p.supplier.contact_name && <p>Contact: {p.supplier.contact_name}</p>}
                      {p.supplier.contact_email && <p>Email: {p.supplier.contact_email}</p>}
                      {p.supplier.contact_phone && <p>Phone: {p.supplier.contact_phone}</p>}
                    </div>
                  </div>
                )}

                {/* Pricing / COGS / margin */}
                <div className="border border-zinc-200 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">Pricing & margin</p>
                    <button onClick={() => setEditPricing(!editPricing)} data-testid="edit-pricing-toggle" className="text-xs font-medium text-[#e84e34] hover:underline">{editPricing ? "Cancel" : "Edit"}</button>
                  </div>
                  {!editPricing ? (
                    <div className="space-y-1 text-sm text-zinc-700">
                      <p>RRP (per unit): <span className="font-medium">{money(p.rrp)}</span></p>
                      {(p.costs || []).length > 0 && (
                        <div className="text-xs text-zinc-500">
                          {p.costs.map((c) => <div key={c.id} className="flex justify-between"><span>{c.label}</span><span>{money(c.amount)}</span></div>)}
                        </div>
                      )}
                      <p>Units: <span className="font-medium">{econ.units}</span></p>
                      <p>Total cost{econ.units > 1 ? ` (${econ.units} units)` : ""}: <span className="font-medium">{money(econ.totalCost)}</span></p>
                      <p>Cost per unit: <span className="font-medium">{money(econ.costPerUnit)}</span></p>
                      <p>Profit per unit: <span className="font-medium">{econ.profit != null ? money(econ.profit) : "—"}</span></p>
                      <p>Margin: <span className="font-medium" style={{ color: econ.margin != null && econ.margin < 0 ? "#e84e34" : "#16a34a" }}>{econ.margin != null ? `${econ.margin.toFixed(1)}%` : "—"}</span></p>
                      <p className="pt-1">Shelf: <span className="font-medium">{p.shelf || "—"}</span></p>
                      <p>URL: {p.product_url ? <a href={p.product_url} target="_blank" rel="noreferrer" className="text-[#e84e34] hover:underline">link</a> : "—"}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div>
                        <label className="mb-1 block text-[11px] text-zinc-400">Selling price / RRP</label>
                        <input type="number" step="0.01" data-testid="edit-rrp" value={pr.rrp} onChange={(e) => setPr({ ...pr, rrp: e.target.value })} className="w-full border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
                      </div>
                      <div>
                        <label className="mb-1 block text-[11px] text-zinc-400">Units in this product</label>
                        <input type="number" min="1" step="1" data-testid="edit-units" value={pr.units} onChange={(e) => setPr({ ...pr, units: e.target.value })} className="w-full border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] text-zinc-400">Costs (COGS)</span>
                        <button type="button" onClick={addCost} data-testid="edit-add-cost" className="flex items-center gap-1 text-xs font-medium text-[#e84e34] hover:underline"><Plus className="h-3 w-3" /> Add</button>
                      </div>
                      {pr.costs.map((c, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <input value={c.label} onChange={(e) => updCost(i, "label", e.target.value)} data-testid={`edit-cost-label-${i}`} placeholder="Cost name" className="flex-1 border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
                          <input type="number" step="0.01" value={c.amount} onChange={(e) => updCost(i, "amount", e.target.value)} data-testid={`edit-cost-amount-${i}`} placeholder="0.00" className="w-24 border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
                          <button type="button" onClick={() => rmCost(i)} className="text-zinc-300 hover:text-[#e84e34]"><X className="h-4 w-4" /></button>
                        </div>
                      ))}
                      <p className="text-xs text-zinc-500">Cost/unit: {money(editCogs / (parseInt(pr.units) || 1))} · Margin: {pr.rrp !== "" && parseFloat(pr.rrp) ? `${(((parseFloat(pr.rrp) - editCogs / (parseInt(pr.units) || 1)) / parseFloat(pr.rrp)) * 100).toFixed(1)}%` : "—"}</p>
                      <label className="mb-0.5 mt-1 block text-[11px] text-zinc-400">Label on the "RRP" field is per single unit. Costs above are the total for all units.</label>
                      <input data-testid="edit-shelf" value={pr.shelf} onChange={(e) => setPr({ ...pr, shelf: e.target.value })} placeholder="Shelf" className="w-full border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
                      <input data-testid="edit-product-url" value={pr.product_url} onChange={(e) => setPr({ ...pr, product_url: e.target.value })} placeholder="Product URL" className="w-full border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
                      <button onClick={savePricing} data-testid="save-pricing" className="bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800">Save</button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Approvers & decisions</label>
                  <div className="space-y-1">
                    {(p.approvers || []).map((a) => {
                      const d = p.decisions?.find((x) => x.user_id === a.id);
                      return (
                        <div key={a.id} className="flex items-center justify-between border border-zinc-200 px-2 py-1.5 text-sm">
                          <span className="text-zinc-700">{a.name}</span>
                          {d ? <span className="text-xs font-medium uppercase" style={{ color: d.decision === "approved" ? "#16a34a" : "#e84e34" }}>{d.decision}</span> : <span className="text-xs text-zinc-400">pending</span>}
                        </div>
                      );
                    })}
                    {(p.approvers || []).length === 0 && <p className="text-xs text-zinc-400">No approvers assigned</p>}
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="text-xs font-medium uppercase tracking-wider text-zinc-500">Attachments</label>
                    <div className="flex items-center gap-3">
                      <DriveImport targetType="product" targetId={productId} onImported={(parent) => { if (parent) setP(parent); onChange?.(); }} />
                      <button onClick={() => fileRef.current?.click()} data-testid="product-upload-button" className="flex items-center gap-1 text-xs font-medium text-[#e84e34] hover:underline"><Paperclip className="h-3 w-3" /> Add</button>
                    </div>
                    <input ref={fileRef} type="file" className="hidden" onChange={upload} />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(p.attachments || []).map((f) => <AttachmentThumb key={f.id} file={f} />)}
                    {(p.attachments || []).length === 0 && <p className="text-xs text-zinc-400">No attachments</p>}
                  </div>
                </div>
              </div>

              <div className="flex flex-col bg-zinc-50/50">
                <div className="border-b border-zinc-200 p-4"><h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-zinc-700">Notes</h3></div>
                <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="product-notes">
                  {notes.length === 0 && <p className="text-sm text-zinc-400">No notes yet</p>}
                  {notes.map((n) => (
                    <div key={n.id} className="border border-zinc-200 bg-white p-2.5">
                      <p className="text-xs font-medium text-zinc-900">{n.user_name} <span className="font-normal text-zinc-400">· {timeAgo(n.created_at)}</span></p>
                      <p className="mt-0.5 whitespace-pre-wrap text-sm text-zinc-700">{n.message}</p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-zinc-200 bg-white p-3">
                  <div className="flex items-end gap-2">
                    <textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" rows={2} className="flex-1 resize-none border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
                    <button onClick={addNote} disabled={busy} data-testid="note-submit" className="flex h-9 w-9 items-center justify-center bg-[#e84e34] text-white hover:bg-[#c73e26] disabled:opacity-60">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
